import { useCallback, useEffect, useRef, useState } from "react";
import { useReveal } from "@/hooks/useReveal";
import { APP_URL } from "@/lib/constants";
import { ArrowRight, ArrowLeft } from "lucide-react";

const ITEMS = [
  { img: "/media/cover-e.jpg", brand: "Angiolini", name: "Кейп с отделкой из соболя", note: "Кашемир · графит" },
  { img: "/media/cover-d.jpg", brand: "Be Florence", name: "Пальто с мехом соболя", note: "Шерсть · орех" },
  { img: "/media/cover-c.jpg", brand: "Be Florence", name: "Жакет с норковой планкой", note: "Альпака · серый меланж" },
  { img: "/media/cover-b.jpg", brand: "MARCO DEL FORTE", name: "Пуховик с фирменным принтом", note: "Лимитированная серия" },
  { img: "/media/cover-a.jpg", brand: "Torriss", name: "Пуховик с меховым капюшоном", note: "Мембрана · чёрный" },
  { img: "/media/cover-f.jpg", brand: "MARCO DEL FORTE", name: "Пуховик с золотистым отливом", note: "Лимитированная серия · золото" },
  { img: "/media/cover-g.jpg", brand: "Torriss", name: "Стёганый пуховик оверсайз", note: "Перламутр · тауп" },
  { img: "/media/cover-h.jpg", brand: "Be Florence", name: "Куртка из кожи с мехом соболя", note: "Винтажная кожа · орех" },
  { img: "/media/cover-i.jpg", brand: "Angiolini", name: "Пуховик-кокон с капюшоном", note: "Небесно-голубой" },
  { img: "/media/cover-j.jpg", brand: "Angiolini", name: "Жакет из меха соболя", note: "Соболь · тёмный" },
  { img: "/media/cover-k.jpg", brand: "Angiolini", name: "Шуба с принтом рыси и шапка", note: "Мех · снежный принт" },
  { img: "/media/cover-l.jpg", brand: "Angiolini", name: "Жакет из меха шиншиллы", note: "Шиншилла · графит" },
  { img: "/media/cover-m.jpg", brand: "Be Florence", name: "Шуба из соболя", note: "Соболь · светлый орех" },
  { img: "/media/cover-n.jpg", brand: "MARCO DEL FORTE", name: "Пальто из экомеха макси", note: "Длина макси · коньяк" },
  { img: "/media/cover-o.jpg", brand: "Torriss", name: "Жакет из меха норки", note: "Норка · чёрный" },
];

export const NewArrivals = () => {
  const ref = useReveal();
  const trackRef = useRef(null);
  const drag = useRef({ down: false, moved: false, startX: 0, startScroll: 0 });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  useEffect(() => {
    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [updateArrows]);

  const scrollByCards = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector(".pw-slide");
    const step = card ? (card.offsetWidth + 16) * 2 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const onPointerDown = (e) => {
    const el = trackRef.current;
    if (!el || e.pointerType === "touch") return;
    drag.current = { down: true, moved: false, startX: e.clientX, startScroll: el.scrollLeft };
  };

  const onPointerMove = (e) => {
    const el = trackRef.current;
    if (!el || !drag.current.down) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 6 && !drag.current.moved) {
      drag.current.moved = true;
      el.classList.add("is-dragging");
    }
    if (drag.current.moved) el.scrollLeft = drag.current.startScroll - dx;
  };

  const endDrag = () => {
    const el = trackRef.current;
    if (!el || !drag.current.down) return;
    drag.current.down = false;
    if (drag.current.moved) {
      el.classList.remove("is-dragging");
      setTimeout(() => { drag.current.moved = false; }, 50);
    }
  };

  const onCardClick = (e) => {
    if (drag.current.moved) e.preventDefault();
  };

  return (
    <section id="new" ref={ref} data-testid="new-arrivals-section" className="py-28 md:py-36 overflow-hidden" style={{ background: "var(--paper)" }}>
      <div className="pw-container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <span className="pw-eyebrow reveal">Новинки</span>
            <h2 className="pw-h2 reveal mt-6 max-w-xl">Свежие поступления из Италии.</h2>
            <p className="reveal mt-6 max-w-md text-[15px] leading-relaxed" style={{ color: "var(--grey-1)" }}>
              Несколько моделей, которые только приехали в шоурум. Полный
              каталог с ценами и размерами — в приложении.
            </p>
          </div>
          <div className="reveal flex items-center gap-4 self-start md:self-auto">
            <a
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="arrivals-cta"
              className="pw-btn pw-btn-dark"
            >
              Смотреть полный каталог
              <ArrowRight size={16} />
            </a>
            <div className="hidden md:flex items-center gap-2">
              <button
                type="button"
                aria-label="Предыдущие модели"
                data-testid="arrivals-prev-btn"
                className="pw-slider-arrow"
                disabled={!canPrev}
                onClick={() => scrollByCards(-1)}
              >
                <ArrowLeft size={18} />
              </button>
              <button
                type="button"
                aria-label="Следующие модели"
                data-testid="arrivals-next-btn"
                className="pw-slider-arrow"
                disabled={!canNext}
                onClick={() => scrollByCards(1)}
              >
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="pw-container">
        <div
          ref={trackRef}
          data-testid="arrivals-slider"
          className="pw-slider-track flex gap-4 overflow-x-auto pb-2 -mx-5 px-5 md:mx-0 md:px-0 cursor-grab select-none"
          onScroll={updateArrows}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
        >
          {ITEMS.map((item, i) => (
            <a
              key={i}
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`arrival-card-${i}`}
              className="pw-slide reveal group block shrink-0 w-[68vw] xs:w-[52vw] sm:w-[40vw] md:w-[30vw] lg:w-[23%] xl:w-[19%]"
              style={{ transitionDelay: `${Math.min(i, 5) * 70}ms` }}
              onClick={onCardClick}
              draggable={false}
            >
              <div className="relative rounded-2xl overflow-hidden aspect-[3/4]">
                <img
                  src={item.img}
                  alt={`${item.brand} — ${item.name}`}
                  loading="lazy"
                  draggable={false}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                  style={{ objectPosition: "center 20%" }}
                />
                <span
                  className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.14em]"
                  style={{ background: "var(--ink)", color: "var(--accent)" }}
                >
                  New
                </span>
                <div
                  className="absolute inset-x-0 bottom-0 p-4 pt-10 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                  style={{ background: "linear-gradient(180deg, transparent, rgba(6,4,10,0.8))" }}
                >
                  <span className="text-white text-[12px] font-semibold flex items-center gap-1">
                    Цена в приложении <ArrowRight size={12} />
                  </span>
                </div>
              </div>
              <div className="mt-3 text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: "var(--grey-2)" }}>
                {item.brand}
              </div>
              <h3 className="mt-1 text-[15px] font-semibold leading-snug" style={{ letterSpacing: "-0.01em" }}>
                {item.name}
              </h3>
              <div className="text-[12px] mt-0.5" style={{ color: "var(--grey-2)" }}>{item.note}</div>
            </a>
          ))}
        </div>

        <div className="flex md:hidden items-center justify-center gap-2 mt-6">
          <button
            type="button"
            aria-label="Предыдущие модели"
            data-testid="arrivals-prev-btn-mobile"
            className="pw-slider-arrow"
            disabled={!canPrev}
            onClick={() => scrollByCards(-1)}
          >
            <ArrowLeft size={18} />
          </button>
          <button
            type="button"
            aria-label="Следующие модели"
            data-testid="arrivals-next-btn-mobile"
            className="pw-slider-arrow"
            disabled={!canNext}
            onClick={() => scrollByCards(1)}
          >
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};
